import React, { useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Row, Col, Form, InputGroup,
} from 'react-bootstrap';
import Delete from 'components/Delete';
import { treePropType } from '../props';
import { distance } from '../utils';
import { DesignContext } from '@aerialytic/canvas-library';
import NumberCellInput from './NumberCellInput';

const TABLE_WIDTH = 820;
const DEFAULT_TRUNK_HEIGHT_FT = 10;

function Trees({ trees, treeUpdateHandler, deleteShapeHandler }) {
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const { resolution } = useContext(DesignContext);

  const syncScroll = (sourceRef, targetRef) => {
    if (!sourceRef.current || !targetRef.current) return;
    targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
  };

  const getRadiusInFeet = (geometry = []) => {
    if (!resolution || !Array.isArray(geometry) || geometry.length < 2) return 0;
    const rawDistance = distance(geometry[0], geometry[1]) * resolution;
    const feet = rawDistance ? (parseFloat(rawDistance) / 30.48) : 0;
    return Number.isFinite(feet) ? feet : 0;
  };

  const getDisplayTrunkHeight = (heightValue, trunkHeightValue, radiusFeet) => {
    if (Number.isFinite(trunkHeightValue)) return Math.max(0, trunkHeightValue);
    if (!Number.isFinite(heightValue)) return '';
    return DEFAULT_TRUNK_HEIGHT_FT;
  };

  const handleHeightChange = (height, index) => {
    const tree = { ...trees[index], height };
    treeUpdateHandler(tree, index);
  };

  const handleTrunkHeightChange = (trunkHeight, index) => {
    const tree = { ...trees[index], trunkHeight };
    if (Number.isFinite(tree.height) && trunkHeight > tree.height) {
      tree.trunkHeight = tree.height;
    }
    treeUpdateHandler(tree, index);
  };

  const handleModelChange = (model, index) => {
    const tree = { ...trees[index], model };
    treeUpdateHandler(tree, index);
  };

  const handleDSMChange = (dsm, index) => {
    const tree = { ...trees[index], dsm };
    if (dsm === 'Remove') {
      tree.height = 0;
      tree.trunkHeight = 0;
    } else {
      tree.height = 20;
      tree.trunkHeight = Number.isFinite(tree.trunkHeight)
        ? tree.trunkHeight
        : DEFAULT_TRUNK_HEIGHT_FT;
    }
    treeUpdateHandler(tree, index);
  };

  return (
    <div>
      <div
        className="mt-3"
        ref={topRef}
        style={{ overflowX: 'auto' }}
        onScroll={() => syncScroll(topRef, bottomRef)}
      >
        <div style={{ width: `${TABLE_WIDTH}px`, height: '1px' }} />
      </div>
      <div
        ref={bottomRef}
        style={{ overflowX: 'auto' }}
        onScroll={() => syncScroll(bottomRef, topRef)}
      >
        <div style={{ width: `${TABLE_WIDTH}px`, height: '1px' }} />
        <Row className="my-2 font-weight-bold flex-nowrap">
          <Col className="tree-cell" style={{ minWidth: '90px' }}>#&nbsp;&nbsp;&nbsp;&nbsp;Row</Col>
          <Col className="tree-cell">Height</Col>
          <Col className="tree-cell">Trunk Height</Col>
          <Col className="tree-cell">Radius</Col>
          <Col className="tree-cell">Shape</Col>
          <Col className="tree-cell">DSM</Col>
          <Col className="tree-cell" style={{ minWidth: '40px' }} />
        </Row>
        {trees.map(({
          id, height, trunkHeight, model, shape, dsm, geometry,
        }, index) => {
          const radiusFeet = getRadiusInFeet(geometry);
          const radiusValue = Number.isFinite(radiusFeet) ? radiusFeet.toFixed(2) : '0.00';
          const resolvedTrunkHeight = getDisplayTrunkHeight(height, trunkHeight, radiusFeet);
          const heightValue = Number.isFinite(height) ? height : '';
          const trunkHeightValue = resolvedTrunkHeight === '' ? '' : resolvedTrunkHeight;

          return (
            <Row key={id} className="flex-nowrap" style={{ height: 40 }}>
              <Col className="tree-cell" style={{ minWidth: '90px' }}>#&nbsp;&nbsp;&nbsp;&nbsp;{index + 1}</Col>
              <Col className="tree-cell">
                <InputGroup size="sm">
                  <NumberCellInput
                    id={`height-${index}`}
                    value={heightValue}
                    disabled={shape === 'Polygon'}
                    onCommit={(v) => handleHeightChange(v, index)}
                  />
                  <InputGroup.Append>
                    <InputGroup.Text>ft</InputGroup.Text>
                  </InputGroup.Append>
                </InputGroup>
              </Col>
              <Col className="tree-cell">
                <InputGroup size="sm">
                  <NumberCellInput
                    id={`trunk-height-${index}`}
                    value={trunkHeightValue}
                    disabled={shape === 'Polygon'}
                    onCommit={(v) => handleTrunkHeightChange(v, index)}
                  />
                  <InputGroup.Append>
                    <InputGroup.Text>ft</InputGroup.Text>
                  </InputGroup.Append>
                </InputGroup>
              </Col>
              <Col className="tree-cell">
                <InputGroup size="sm">
                  <Form.Control
                    size="sm"
                    value={radiusValue}
                    disabled
                  />
                  <InputGroup.Append>
                    <InputGroup.Text>ft</InputGroup.Text>
                  </InputGroup.Append>
                </InputGroup>
              </Col>
              <Col className="tree-cell">
                <Form.Control
                  size="sm"
                  as="select"
                  id={`model-${index}`}
                  value={model}
                  onChange={(e) => handleModelChange(e.target.value, index)}
                  disabled={shape === 'Polygon'}
                >
                  <option value="Cone">Cone</option>
                  <option value="Sphere">Sphere</option>
                </Form.Control>
              </Col>
              <Col className="tree-cell">
                <Form.Control
                  size="sm"
                  as="select"
                  id={`dsm-${index}`}
                  value={dsm}
                  onChange={(e) => handleDSMChange(e.target.value, index)}
                >
                  <option value="Keep">Keep</option>
                  <option value="Remove">Remove</option>
                </Form.Control>
              </Col>
              <Col className="tree-cell" style={{ minWidth: '40px' }}>
                <Delete id={index} onClick={() => deleteShapeHandler(id)} />
              </Col>
            </Row>
          );
        })}
      </div>
      <style>{`
        .tree-cell {
          padding: 0rem 0.1em;
          white-space: nowrap;
          width: 120px;
          min-width: 120px;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

Trees.propTypes = {
  trees: treePropType.isRequired,
  treeUpdateHandler: PropTypes.func.isRequired,
  deleteShapeHandler: PropTypes.func.isRequired,
};

export default Trees;
